import { AccessFilter, MgmtApi } from '@/customApiClients/mgmtApiClient/Api';
import {
    BrainCircuit,
    ChevronsUpDown,
    LucideIcon,
    Podcast,
    Puzzle,
    Sparkles,
    Upload
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { AccessChecklist } from './AccessChecklist';
import { Button } from '@/components/ui/button';
import { FluidHoverButtonRow } from '@/components/ui/fluid-hover-row';
import { FluidHoverGroup } from '@/components/ui/fluid-hover-group';
import { ListSkeleton } from '@/components/ui/list-skeleton';
import { PillTabsList } from '@/components/ui/pill-tabs-list';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';
import { SearchBar } from '@/components/ui/search-bar';
import { SectionHeader } from '@/components/ui/section-header';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Tabs, TabsContent } from '@/components/ui/tabs';

// Matched by displayName since featureId isn't a stable slug — falls back to
// a generic icon for any feature this list doesn't know about yet.
const FEATURE_ICONS: { match: RegExp; icon: LucideIcon }[] = [
    { match: /podcast/i, icon: Podcast },
    { match: /file upload/i, icon: Upload },
    { match: /suggested prompts/i, icon: Sparkles },
    { match: /scheduled jobs/i, icon: BrainCircuit }
];

const getFeatureIcon = (displayName: string): LucideIcon =>
    FEATURE_ICONS.find((f) => f.match.test(displayName))?.icon ?? Puzzle;

// Collapses a (potentially long) feature list into a short label so the
// access column never has to grow wider than its cell.
const getAccessSummary = (
    currentFeatures: any[],
    allFeatures: { featureId: string; displayName: string }[]
): string => {
    const count = currentFeatures.length;
    if (count === 0) return 'No access';
    if (allFeatures.length > 0 && count === allFeatures.length) return 'All';
    return `${count} feature${count === 1 ? '' : 's'}`;
};

const FEATURE_ACCESS_TABS = [
    { value: 'fa-users', label: 'User access' },
    { value: 'fa-groups', label: 'Group access' },
    { value: 'fa-org', label: 'Organization' }
];

const FilterSelect = ({
    value,
    onChange
}: {
    value: AccessFilter;
    onChange: (v: AccessFilter) => void;
}) => (
    <Select value={value} onValueChange={(v) => onChange(v as AccessFilter)}>
        <SelectTrigger className="w-36">
            <SelectValue />
        </SelectTrigger>
        <SelectContent>
            <SelectItem value={AccessFilter.All}>All</SelectItem>
            <SelectItem value={AccessFilter.Yes}>Has access</SelectItem>
            <SelectItem value={AccessFilter.No}>No access</SelectItem>
        </SelectContent>
    </Select>
);

const Pagination = ({
    page,
    total,
    onPageChange
}: {
    page: number;
    total: number;
    onPageChange: (p: number) => void;
}) => (
    <div className="flex justify-between items-center mt-4 text-muted-foreground text-sm">
        <span>
            {total === 0
                ? 'No results'
                : `Showing ${(page - 1) * 10 + 1}–${Math.min(
                      page * 10,
                      total
                  )} of ${total} results`}
        </span>
        <div className="flex gap-2">
            <Button
                variant="tertiary"
                size="default"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
            >
                Previous
            </Button>
            <Button
                variant="tertiary"
                size="default"
                disabled={page * 10 >= total}
                onClick={() => onPageChange(page + 1)}
            >
                Next
            </Button>
        </div>
    </div>
);

const FeaturesPopover = ({
    currentFeatures,
    isOpen,
    onOpenChange,
    onSave,
    allFeatures,
    pendingFeatureIds,
    setPendingFeatureIds,
    saving
}: {
    currentFeatures: any[];
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onSave: (featureIds: string[]) => void;
    allFeatures: { featureId: string; displayName: string }[];
    pendingFeatureIds: string[];
    setPendingFeatureIds: React.Dispatch<React.SetStateAction<string[]>>;
    saving: boolean;
}) => (
    <Popover
        open={isOpen}
        onOpenChange={(open) => {
            if (open)
                setPendingFeatureIds(
                    currentFeatures.map((f: any) => f.featureId)
                );
            onOpenChange(open);
        }}
    >
        <PopoverTrigger asChild>
            <Button
                variant="ghost"
                size="default"
                className="px-2 py-1.5 max-w-full h-auto font-normal text-muted-foreground hover:text-foreground text-sm"
            >
                <span
                    className="min-w-0 truncate"
                    title={
                        currentFeatures.length === 0
                            ? undefined
                            : currentFeatures
                                  .map((f: any) => f.displayName)
                                  .join(', ')
                    }
                >
                    {getAccessSummary(currentFeatures, allFeatures)}
                </span>
                <ChevronsUpDown className="shrink-0" />
            </Button>
        </PopoverTrigger>
        <PopoverContent className="bg-background p-0 w-80" align="start">
            <AccessChecklist
                title="Features"
                items={allFeatures.map((f) => ({
                    id: f.featureId,
                    label: f.displayName
                }))}
                selectedIds={pendingFeatureIds}
                onSelectedIdsChange={setPendingFeatureIds}
                emptyText="No features available"
                saving={saving}
                onCancel={() => onOpenChange(false)}
                onSave={onSave}
            />
        </PopoverContent>
    </Popover>
);

interface Props {
    mgmtApi: MgmtApi<unknown>;
}

export const FeatureAccessTab: React.FC<Props> = ({ mgmtApi }) => {
    // --- Org feature toggles (mgmt-backed) ---
    const [orgFeatureIds, setOrgFeatureIds] = useState<string[]>([]);
    const [orgAlias, setOrgAlias] = useState<string>('');
    const [orgName, setOrgName] = useState<string>('');
    const [orgLoading, setOrgLoading] = useState(false);
    const [orgSaving, setOrgSaving] = useState<string | null>(null);

    // --- User/Group access state ---
    const [featureUsers, setFeatureUsers] = useState<any[]>([]);
    const [featureUsersTotal, setFeatureUsersTotal] = useState(0);
    const [featureUsersPage, setFeatureUsersPage] = useState(1);
    const [featureUsersSearch, setFeatureUsersSearch] = useState('');
    const [featureUsersDebouncedSearch, setFeatureUsersDebouncedSearch] =
        useState('');
    const [featureUsersFilter, setFeatureUsersFilter] = useState<AccessFilter>(
        AccessFilter.All
    );
    const [featureUsersLoading, setFeatureUsersLoading] = useState(false);

    const [featureGroups, setFeatureGroups] = useState<any[]>([]);
    const [featureGroupsTotal, setFeatureGroupsTotal] = useState(0);
    const [featureGroupsPage, setFeatureGroupsPage] = useState(1);
    const [featureGroupsSearch, setFeatureGroupsSearch] = useState('');
    const [featureGroupsDebouncedSearch, setFeatureGroupsDebouncedSearch] =
        useState('');
    const [featureGroupsFilter, setFeatureGroupsFilter] =
        useState<AccessFilter>(AccessFilter.All);
    const [featureGroupsLoading, setFeatureGroupsLoading] = useState(false);

    const [allFeatures, setAllFeatures] = useState<
        { featureId: string; displayName: string }[]
    >([]);
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [pendingFeatureIds, setPendingFeatureIds] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => {
            setFeatureUsersDebouncedSearch(featureUsersSearch);
            setFeatureUsersPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [featureUsersSearch]);

    useEffect(() => {
        const t = setTimeout(() => {
            setFeatureGroupsDebouncedSearch(featureGroupsSearch);
            setFeatureGroupsPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [featureGroupsSearch]);

    const mergeFeatures = (incoming: any[]) => {
        setAllFeatures((prev) => {
            const map = new Map(prev.map((f) => [f.featureId, f]));
            incoming.forEach((f: any) => map.set(f.featureId, f));
            return Array.from(map.values());
        });
    };

    useEffect(() => {
        setOrgLoading(true);
        mgmtApi.api
            .v1AccessFeaturesOrganizationsList()
            .then((res) => {
                const data = res?.data;
                setOrgAlias(data?.alias || '');
                setOrgName(data?.name || '');
                setOrgFeatureIds(
                    (data?.features || []).map((f: any) => f.featureId)
                );
            })
            .catch(() => {})
            .finally(() => setOrgLoading(false));
    }, [mgmtApi]);

    const toggleOrgFeature = async (featureId: string, enabled: boolean) => {
        const prevIds = orgFeatureIds;
        const newIds = enabled
            ? [...orgFeatureIds, featureId]
            : orgFeatureIds.filter((id) => id !== featureId);
        setOrgFeatureIds(newIds); // optimistic
        setOrgSaving(featureId);
        try {
            await mgmtApi.api.v1AccessFeaturesOrganizationsUpdate({
                alias: orgAlias,
                featureIds: newIds
            });
        } catch {
            setOrgFeatureIds(prevIds); // revert
        } finally {
            setOrgSaving(null);
        }
    };

    useEffect(() => {
        mgmtApi.api
            .v1AccessFeaturesList()
            .then((res) => setAllFeatures(res?.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        setFeatureUsersLoading(true);
        mgmtApi.api
            .v1AccessFeaturesUsersList({
                Page: featureUsersPage,
                Size: 10,
                Filter: featureUsersDebouncedSearch || undefined,
                AccessFilter: featureUsersFilter,
                IsDescending: false
            })
            .then((res) => {
                setFeatureUsers(res?.data?.data || []);
                setFeatureUsersTotal(res?.data?.rows || 0);
                (res?.data?.data || []).forEach((u: any) =>
                    mergeFeatures(u.features || [])
                );
            })
            .catch(console.error)
            .finally(() => setFeatureUsersLoading(false));
    }, [
        mgmtApi,
        featureUsersPage,
        featureUsersDebouncedSearch,
        featureUsersFilter
    ]);

    useEffect(() => {
        setFeatureGroupsLoading(true);
        mgmtApi.api
            .v1AccessFeaturesGroupsList({
                Page: featureGroupsPage,
                Size: 10,
                Filter: featureGroupsDebouncedSearch || undefined,
                AccessFilter: featureGroupsFilter,
                IsDescending: false
            })
            .then((res) => {
                setFeatureGroups(res?.data?.data || []);
                setFeatureGroupsTotal(res?.data?.rows || 0);
                (res?.data?.data || []).forEach((g: any) =>
                    mergeFeatures(g.features || [])
                );
            })
            .catch(console.error)
            .finally(() => setFeatureGroupsLoading(false));
    }, [
        mgmtApi,
        featureGroupsPage,
        featureGroupsDebouncedSearch,
        featureGroupsFilter
    ]);

    const handleSaveUser = async (userId: string, featureIds: string[]) => {
        setSaving(true);
        try {
            await mgmtApi.api.v1AccessFeaturesUsersUpdate({
                userId,
                featureIds
            });
            setFeatureUsers((prev) =>
                prev.map((u) =>
                    u.userId === userId
                        ? {
                              ...u,
                              features: allFeatures.filter((f) =>
                                  featureIds.includes(f.featureId)
                              )
                          }
                        : u
                )
            );
        } catch (e) {
            console.error('Failed to save feature user access', e);
        } finally {
            setSaving(false);
            setEditingUserId(null);
        }
    };

    const handleSaveGroup = async (groupId: string, featureIds: string[]) => {
        setSaving(true);
        try {
            await mgmtApi.api.v1AccessFeaturesGroupsUpdate({
                groupId,
                featureIds
            });
            setFeatureGroups((prev) =>
                prev.map((g) =>
                    g.groupId === groupId
                        ? {
                              ...g,
                              features: allFeatures.filter((f) =>
                                  featureIds.includes(f.featureId)
                              )
                          }
                        : g
                )
            );
        } catch (e) {
            console.error('Failed to save feature group access', e);
        } finally {
            setSaving(false);
            setEditingGroupId(null);
        }
    };

    return (
        <div className="space-y-8">
            <section className="space-y-6">
                <SectionHeader
                    title="Feature Access"
                    description="Manage user and group access permissions for features"
                />
                <Tabs defaultValue="fa-users">
                    <PillTabsList tabs={FEATURE_ACCESS_TABS} className="mb-6" />

                    <TabsContent value="fa-users">
                        <div className="flex items-center gap-3 mb-4">
                            <SearchBar
                                value={featureUsersSearch}
                                onChange={(v) => {
                                    setFeatureUsersSearch(v);
                                }}
                                placeholder="Search users..."
                            />
                            <FilterSelect
                                value={featureUsersFilter}
                                onChange={(v) => {
                                    setFeatureUsersFilter(v);
                                    setFeatureUsersPage(1);
                                }}
                            />
                        </div>
                        {featureUsersLoading ? (
                            <TableSkeleton columns={4} />
                        ) : (
                            <>
                                <div>
                                    <Table variant="fluid" className="table-fixed">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>
                                                    Display Name
                                                </TableHead>
                                                <TableHead>
                                                    Email
                                                </TableHead>
                                                <TableHead className="w-36">
                                                    Features
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {featureUsers.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={3}
                                                        className="py-8 text-muted-foreground text-center"
                                                    >
                                                        No users found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                featureUsers.map(
                                                    (user: any) => (
                                                        <TableRow
                                                            key={user.userId}
                                                        >
                                                            <TableCell
 className="font-medium truncate"
 title={user.displayName}
 >
 {user.displayName}
 </TableCell>
                                                            <TableCell
                                                                className="text-muted-foreground truncate"
                                                                title={
                                                                    user.email
                                                                }
                                                            >
                                                                {user.email}
                                                            </TableCell>
                                                            <TableCell>
                                                                <FeaturesPopover
                                                                    currentFeatures={
                                                                        user.features ||
                                                                        []
                                                                    }
                                                                    isOpen={
                                                                        editingUserId ===
                                                                        user.userId
                                                                    }
                                                                    onOpenChange={(
                                                                        open
                                                                    ) =>
                                                                        setEditingUserId(
                                                                            open
                                                                                ? user.userId
                                                                                : null
                                                                        )
                                                                    }
                                                                    onSave={(
                                                                        ids
                                                                    ) =>
                                                                        handleSaveUser(
                                                                            user.userId,
                                                                            ids
                                                                        )
                                                                    }
                                                                    allFeatures={
                                                                        allFeatures
                                                                    }
                                                                    pendingFeatureIds={
                                                                        pendingFeatureIds
                                                                    }
                                                                    setPendingFeatureIds={
                                                                        setPendingFeatureIds
                                                                    }
                                                                    saving={
                                                                        saving
                                                                    }
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                                <Pagination
                                    page={featureUsersPage}
                                    total={featureUsersTotal}
                                    onPageChange={setFeatureUsersPage}
                                />
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="fa-groups">
                        <div className="flex items-center gap-3 mb-4">
                            <SearchBar
                                value={featureGroupsSearch}
                                onChange={(v) => {
                                    setFeatureGroupsSearch(v);
                                }}
                                placeholder="Search groups..."
                            />
                            <FilterSelect
                                value={featureGroupsFilter}
                                onChange={(v) => {
                                    setFeatureGroupsFilter(v);
                                    setFeatureGroupsPage(1);
                                }}
                            />
                        </div>
                        {featureGroupsLoading ? (
                            <TableSkeleton columns={3} />
                        ) : (
                            <>
                                <div>
                                    <Table variant="fluid" className="table-fixed">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>
                                                    Group Name
                                                </TableHead>
                                                <TableHead>
                                                    Members
                                                </TableHead>
                                                <TableHead>
                                                    Features
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {featureGroups.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={3}
                                                        className="py-8 text-muted-foreground text-center"
                                                    >
                                                        No groups found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                featureGroups.map(
                                                    (group: any) => (
                                                        <TableRow
                                                            key={group.groupId}
                                                        >
                                                            <TableCell
                                                                className="font-medium truncate"
                                                                title={
                                                                    group.groupName
                                                                }
                                                            >
                                                                {
                                                                    group.groupName
                                                                }
                                                            </TableCell>
                                                            <TableCell className="text-muted-foreground">
                                                                {group.members ??
                                                                    '—'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <FeaturesPopover
                                                                    currentFeatures={
                                                                        group.features ||
                                                                        []
                                                                    }
                                                                    isOpen={
                                                                        editingGroupId ===
                                                                        group.groupId
                                                                    }
                                                                    onOpenChange={(
                                                                        open
                                                                    ) =>
                                                                        setEditingGroupId(
                                                                            open
                                                                                ? group.groupId
                                                                                : null
                                                                        )
                                                                    }
                                                                    onSave={(
                                                                        ids
                                                                    ) =>
                                                                        handleSaveGroup(
                                                                            group.groupId,
                                                                            ids
                                                                        )
                                                                    }
                                                                    allFeatures={
                                                                        allFeatures
                                                                    }
                                                                    pendingFeatureIds={
                                                                        pendingFeatureIds
                                                                    }
                                                                    setPendingFeatureIds={
                                                                        setPendingFeatureIds
                                                                    }
                                                                    saving={
                                                                        saving
                                                                    }
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                                <Pagination
                                    page={featureGroupsPage}
                                    total={featureGroupsTotal}
                                    onPageChange={setFeatureGroupsPage}
                                />
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="fa-org">
                        {orgLoading ? (
                            <ListSkeleton rows={4} />
                        ) : (
                            <div className="overflow-hidden">
                                <div className="px-5 py-4 border-border border-b">
                                    <p className="font-medium text-foreground text-base">
                                        {orgName || 'Organization'}
                                    </p>
                                    <p className="mt-0.5 text-muted-foreground text-sm">
                                        Grant all users in{' '}
                                        <span className="font-medium text-foreground">
                                            {orgName || 'the organization'}
                                        </span>{' '}
                                        access to the features below.
                                    </p>
                                </div>
                                <FluidHoverGroup className="divide-y divide-border">
                                    {allFeatures.map((f) => {
                                        const FeatureIcon = getFeatureIcon(
                                            f.displayName
                                        );
                                        return (
                                            <FluidHoverButtonRow
                                                key={f.featureId}
                                                disabled={
                                                    orgSaving === f.featureId
                                                }
                                                onClick={() =>
                                                    toggleOrgFeature(
                                                        f.featureId,
                                                        !orgFeatureIds.includes(
                                                            f.featureId
                                                        )
                                                    )
                                                }
                                                className="flex justify-between items-center gap-4 px-5 py-3.5 disabled:opacity-50 transition-colors disabled:cursor-not-allowed"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="flex justify-center items-center border rounded-md w-8 h-8 shrink-0">
                                                        <FeatureIcon className="w-4 h-4 text-secondary-foreground" />
                                                    </div>
                                                    <span className="font-medium text-foreground text-sm">
                                                        {f.displayName}
                                                    </span>
                                                </div>
                                                <Switch
                                                    checked={orgFeatureIds.includes(
                                                        f.featureId
                                                    )}
                                                    disabled={
                                                        orgSaving ===
                                                        f.featureId
                                                    }
                                                    tabIndex={-1}
                                                    className="pointer-events-none"
                                                    size="sm"
                                                />
                                            </FluidHoverButtonRow>
                                        );
                                    })}
                                    {allFeatures.length === 0 && (
                                        <div className="px-5 py-8 text-muted-foreground text-sm text-center">
                                            No features available
                                        </div>
                                    )}
                                </FluidHoverGroup>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </section>
        </div>
    );
};
